import { useState } from "react";
import { formatDateDDMonYY } from "@/utils/dateFormat";
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

/**
 * A date input that displays in DD-Mon-YY format (e.g., 25-Mar-26)
 * Uses Shadcn Popover + Calendar for reliable cross-browser/iframe support.
 */
export const DateInputDDMonYY = ({ value, onChange, className = "" }) => {
  const [open, setOpen] = useState(false);

  // Parse YYYY-MM-DD string to Date
  const dateValue = (() => {
    if (!value) return undefined;
    const [y, m, d] = String(value).split('-').map(Number);
    return new Date(y, m - 1, d);
  })();

  const handleSelect = (date) => {
    if (!date) return;
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const isoStr = `${yyyy}-${mm}-${dd}`;
    // Mimic native onChange event shape
    onChange({ target: { value: isoStr } });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center border rounded-md bg-white cursor-pointer hover:border-gray-400 transition-colors gap-1 ${className}`}
          data-testid="date-input-ddmonyy"
        >
          <span className="px-2 text-sm select-none">
            {value ? formatDateDDMonYY(value) : "Select date"}
          </span>
          <CalendarIcon className="w-3.5 h-3.5 text-muted-foreground mr-2 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};
